import { Helmet } from 'react-helmet';
import React, { useState, useEffect, useRef } from 'react';
import {
    getUserFiles, deleteFile, updateFile, createProject, getUserFilesStatus, getUsersWithFileAccess, fileShare,
    delFileUserShare, getProjecUsers, getUserById, getSharedFiles, getFileDetails
} from './../api';
import { Table, Modal, Button, Form, ProgressBar, Badge, Pagination, Dropdown } from 'react-bootstrap';
import UploadFilesTreading from "../components/upload-files-threading.component";
import OCRDetailsModal from "../components/OCRDetailsModal";
import { toast } from 'react-toastify';
import useAuth from '../hooks/useAuth';

function Resources() {
    const title = 'Resources';
    const auth = useAuth();
    const user = auth.getSession();
    const [isEnterpriseUser, setIsEnterpriseUser] = useState(false);
    const [files, setFiles] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentFile, setCurrentFile] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [allSelected, setAllSelected] = useState(false);
    const [showHints, setShowHints] = useState(() => {
        // Initialize state from localStorage, default to false if not set
        const saved = localStorage.getItem('showHints');
        return saved !== null ? JSON.parse(saved) : false;
    });
    // State to track processing files (will be shown in upload modal when reopened)
    const [processingFiles, setProcessingFiles] = useState([]);

    // Create a ref to store the polling interval ID
    const pollingIntervalRef = useRef(null);
    // Create a ref for modal-specific polling
    const modalPollingRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [listsPerPage] = useState(10);

    const [showShareModal, setShowShareModal] = useState(false);
    const [sharedUsers, setSharedUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [sharedFiles, setSharedFiles] = useState([]);
    const [fileFilter, setFileFilter] = useState('all'); // 'all', 'owned', or 'shared'
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchUserFiles();
        fetchSharedFiles();
        fetchAllUsers();
        // Start main polling when component mounts
        startMainPolling();
        setIsEnterpriseUser(user.is_enterprise);
        // Clean up all intervals when component unmounts
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            if (modalPollingRef.current) {
                clearInterval(modalPollingRef.current);
            }
        };
    }, []);

    const fetchAllUsers = async () => {
        try {
            const response = await getProjecUsers();
            setAllUsers(response.data);
        } catch (error) {
            console.error('Error fetching all users:', error);
        }
    };
    // Function to check all files status (used in main polling)
    const checkAllFilesStatus = async () => {
        console.log("Checking all files status...");
        try {
            const response = await getUserFiles();
            const allFiles = response.data;

            // Find files that are still processing
            const filesStillProcessing = allFiles.filter(file => isFileProcessing(file));
            console.log(`Found ${filesStillProcessing.length} files still processing`);

            // Update state with a new array reference to ensure React detects the change
            const newFilesArray = JSON.parse(JSON.stringify(allFiles));
            setFiles(newFilesArray);

            // Update the processingFiles state for use in the modal
            setProcessingFiles(filesStillProcessing);

            // If no files are processing, we can stop the main polling
            if (filesStillProcessing.length === 0 && pollingIntervalRef.current) {
                console.log("No files processing, stopping main poll");
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        } catch (error) {
            console.error("Error checking file status:", error);
        }
    };

    // Start the main polling interval
    const startMainPolling = () => {
        console.log("Starting main polling...");
        // Clear any existing interval
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        // Set a new interval to check all files
        pollingIntervalRef.current = setInterval(() => {
            checkAllFilesStatus();
        }, 3000); // Check every 3 seconds
    };

    // Function for targeted polling of specific files
    const checkSpecificFilesStatus = async (fileIds) => {
        console.log("Checking specific files:", fileIds);
        if (!fileIds || fileIds.length === 0) return;

        try {
            const response = await getUserFilesStatus(fileIds);
            if (response && response.data && Array.isArray(response.data)) {
                // Update the main files state
                setFiles(prevFiles => {
                    return prevFiles.map(file => {
                        const updatedFile = response.data.find(update => update.id === file.id);
                        if (updatedFile) {
                            return { ...file, ...updatedFile };
                        }
                        return file;
                    });
                });

                // Also update the processingFiles state
                setProcessingFiles(prevFiles => {
                    // Update existing processing files
                    const updatedFiles = prevFiles.map(file => {
                        const updated = response.data.find(u => u.id === file.id);
                        return updated ? { ...file, ...updated } : file;
                    });

                    // Only keep files that are still processing
                    return updatedFiles.filter(file => isFileProcessing(file));
                });

                console.log("ProcessingFiles:", processingFiles);
            }
        } catch (error) {
            console.error("Error checking specific files:", error);
        }
    };

    // Modal-specific polling effect
    useEffect(() => {
        // Only run polling while the upload modal is open
        if (showUploadModal) {
            console.log("Starting modal-specific polling");

            // Initial fetch of processing files when modal opens
            const fetchInitialProcessingFiles = async () => {
                const response = await getUserFiles();
                const processingOnes = response.data.filter(file => isFileProcessing(file));
                setProcessingFiles(processingOnes);

                // If there are processing files, set isUploading to true
                if (processingOnes.length > 0) {
                    setIsUploading(true);
                }
            };

            fetchInitialProcessingFiles();

            // Set up interval specifically for the modal
            modalPollingRef.current = setInterval(() => {
                // Only poll if we have processing files
                if (processingFiles.length > 0) {
                    const processingIds = processingFiles.map(file => file.id);
                    checkSpecificFilesStatus(processingIds);
                }
            }, 2000);

            // Clean up when modal closes
            return () => {
                if (modalPollingRef.current) {
                    clearInterval(modalPollingRef.current);
                    modalPollingRef.current = null;
                }
            };
        }
    }, [showUploadModal, processingFiles.length]);

    const fetchUserFiles = async () => {
        console.log("Fetching all user files...");
        try {
            const response = await getUserFiles();
            setFiles(response.data);

            // Also update processing files
            const filesInProgress = response.data.filter(file => isFileProcessing(file));
            setProcessingFiles(filesInProgress);

            // If we have processing files, make sure main polling is active
            if (filesInProgress.length > 0 && !pollingIntervalRef.current) {
                startMainPolling();
            }
            console.log("File:", response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching user files:', error);
        }
    };

    const fetchSharedFiles = async () => {
        try {
            const response = await getSharedFiles();
            const sharedFilesData = response.data.map(file => ({
                ...file,
                isShared: true
            }));
            setSharedFiles(sharedFilesData);
        } catch (error) {
            console.error('Error fetching shared files:', error);
        }
    };

    // Get combined files for unified list view
    const getAllFiles = () => {
        switch (fileFilter) {
            case 'owned':
                return files;
            case 'shared':
                return sharedFiles;
            case 'all':
            default:
                return [...files, ...sharedFiles];
        }
    };

    // Helper function to format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    const formatReadability = (ocrConfidence) => {
        if (ocrConfidence === null || ocrConfidence === undefined) {
            return '100%';
        }
        return `${Math.round(ocrConfidence * 10) / 10}%`;
    };

    const handleUploadShow = () => {
        setShowUploadModal(true);
        // When opening the modal, do an immediate check for processing files
        fetchUserFiles();
    };
    const handleProjectClose = () => setShowProjectModal(false);
    const handleProjectShow = () => setShowProjectModal(true);

    // Function to handle modal close attempt
    const handleUploadClose = () => {
        if (isUploading) {
            if (window.confirm("Vector is in progress. The Vector will continue in the background, but you won't see its progress. Are you sure you want to close this window?")) {
                setShowUploadModal(false);

                // Immediately fetch latest files
                fetchUserFiles().then(updatedFiles => {
                    // Make sure main polling is active if we have processing files
                    const hasProcessingFiles = updatedFiles.some(file => isFileProcessing(file));
                    if (hasProcessingFiles && !pollingIntervalRef.current) {
                        startMainPolling();
                    }
                });
            }
        } else {
            setShowUploadModal(false);
            fetchUserFiles();
        }
    };
    const handleUploadingStatus = (status) => {
        setIsUploading(status);
    };
    const handleEditShow = (file) => {
        setCurrentFile(file);
        setShowEditModal(true);
    };
    const handleEditClose = () => {
        setShowEditModal(false);
        setCurrentFile(null);
    };

    const handleDeleteShow = (file) => {
        setCurrentFile(file);
        setShowDeleteModal(true);
    };
    const handleDeleteClose = () => {
        setShowDeleteModal(false);
        setCurrentFile(null);
    };

    const handleEditSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        const fileData = Object.fromEntries(formData.entries());
        try {
            await updateFile(currentFile.id, fileData);
            fetchUserFiles();
            handleEditClose();
        } catch (error) {
            console.error('Error updating file:', error);
        }
    };

    const handleDeleteFile = async () => {
        try {
            await deleteFile(currentFile.id);
            fetchUserFiles();
            handleDeleteClose();
        } catch (error) {
            console.error('Error deleting file:', error);
        }
    };

    const filteredFiles = getAllFiles().filter(file =>
        file.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleProjectSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        const projectData = Object.fromEntries(formData.entries());

        try {
            // Add selected files to project data
            projectData.files = selectedFiles.map(fileId => {
                const file = getAllFiles().find(f => f.id === fileId);
                return { id: fileId, name: file.name };
            });
            await createProject(projectData);

            handleProjectClose();
        } catch (error) {
            console.error('Error saving project:', error);
        }
    };

    const toggleSelectAll = () => {
        setAllSelected(!allSelected);
        if (!allSelected) {
            setSelectedFiles(files.map(file => file.id));
        } else {
            setSelectedFiles([]);
        }
    };

    const handleFileSelect = (fileId) => {
        console.log("fileId=", fileId);
        console.log("SelectedFiles:", selectedFiles);
        setSelectedFiles(prevSelected => {
            if (prevSelected.includes(fileId)) {
                return prevSelected.filter(id => id !== fileId);
            } else {
                return [...prevSelected, fileId];
            }
        });
    };

    const handleUploadComplete = () => {
        setIsUploading(false);

        // Important: Don't hide the modal just yet if there are still files processing
        if (processingFiles.length === 0) {
            setShowUploadModal(false);
        }

        // Fetch latest files and ensure polling is active
        fetchUserFiles().then(latestFiles => {
            // Check if any files are still processing
            const stillProcessing = latestFiles.some(file => isFileProcessing(file));

            // If files are processing, make sure polling is active
            if (stillProcessing && !pollingIntervalRef.current) {
                startMainPolling();
            }
        });
    };

    useEffect(() => {
        // Update localStorage whenever showHints changes
        localStorage.setItem('showHints', JSON.stringify(showHints));
    }, [showHints]);

    const toggleHints = () => {
        setShowHints(prevState => !prevState);
    };

    const renderFileStatus = (file) => {
        // Handle case when status isn't available
        if (!file.status || file.status === 'completed') {
            return (
                <span className="badge bg-success">Completed</span>
            );
        }

        // For files still processing
        if (file.status === 'processing' || file.status === 'embedding' ||
            file.status === 'vectoring' || file.status === 'preprocessing' ||
            file.status === 'extracting_text' || file.status === 'cleaning') {
            return (
                <div>
                    <div className="d-flex align-items-center">
                        <span className="badge bg-info me-2">Processing</span>
                        {file.progress && file.progress < 100 && (
                            <span className="small">{file.progress}%</span>
                        )}
                    </div>
                    {file.progress && file.progress < 100 && (
                        <div className="progress mt-1" style={{ height: '5px' }}>
                            <div
                                className="progress-bar progress-bar-striped progress-bar-animated"
                                role="progressbar"
                                style={{ width: `${file.progress}%` }}
                                aria-valuenow={file.progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>
                        </div>
                    )}
                </div>
            );
        }

        // For error states
        if (file.status === 'error') {
            return (
                <span className="badge bg-danger">Error</span>
            );
        }

        // For any other status
        return (
            <span className="badge bg-secondary">{file.status}</span>
        );
    };

    const isFileProcessing = (file) => {
        return file.status === 'vectoring' ||
            file.status === 'processing' ||
            file.status === 'embedding' ||
            file.status === 'preprocessing' ||
            file.status === 'extracting_text' ||
            file.status === 'cleaning' ||
            (file.progress && file.progress < 100);
    };

    const indexOfLast = currentPage * listsPerPage;
    const indexOfFirst = indexOfLast - listsPerPage;
    const currentLists = filteredFiles.slice(indexOfFirst, indexOfLast);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const pageNumbers = [];
    for (let i = 1; i <= Math.ceil(filteredFiles.length / listsPerPage); i++) {
        pageNumbers.push(i);
    }

    const handleShareShow = (file) => {
        setCurrentFile(file);
        setSelectedUser('');
        fetchSharedUsers(file.id);
        setShowShareModal(true);
    };
    const handleDetailsShow = (file) => {
        setCurrentFile(file);
        setShowDetailsModal(true);
    };
    const handleDetailsClose = () => {
        setShowDetailsModal(false);
        setCurrentFile(null);
    };
    const handleShareClose = () => {
        setShowShareModal(false);
        setCurrentFile(null);
        setSharedUsers([]);
    };
    const fetchSharedUsers = async (fileId) => {
        try {
            const response = await getUsersWithFileAccess(fileId);
            const file_data = response.data;
            //setSharedUsers(response.data || []);
            if (file_data.shared_with && file_data.shared_with.length > 0) {
                const sharedWithDetails = [];

                for (const share of file_data.shared_with) {
                    // Find user details from users list or make a separate call
                    try {
                        const userResponse = await getUserById(share.user_id);
                        console.log("userResponse:", userResponse);
                        sharedWithDetails.push({
                            ...share,
                            name: `${userResponse.data.firstname} ${userResponse.data.lastname}`,
                            email: userResponse.data.email
                        });

                    } catch (error) {
                        // If user details can't be fetched, use what we have
                        sharedWithDetails.push({
                            ...share,
                            name: 'Unknown User',
                            email: share.user_id
                        });
                    }
                }
                setSharedUsers(sharedWithDetails);
            } else {
                setSharedUsers([]);
            }

        } catch (error) {
            console.error('Error fetching shared users:', error);
        }
    };

    // Function to share a file with a user
    const handleShareFile = async () => {
        if (!selectedUser || !currentFile) {
            toast.error('Please select a user');
            return;
        }

        try {
            await fileShare(currentFile.id, { user_id: selectedUser });
            setSelectedUser('');
            fetchSharedUsers(currentFile.id);
            toast.success('File shared successfully');
        } catch (error) {
            console.error('Error sharing file:', error);
            toast.error(error.response?.data?.error || 'Failed to share file');
        }
    };

    // Function to remove file sharing
    const handleRemoveShare = async (userId) => {
        if (!currentFile) return;
        try {
            await delFileUserShare(currentFile.id, userId);
            fetchSharedUsers(currentFile.id);
            toast.success('User access removed successfully');
        } catch (error) {
            console.error('Error removing shared access:', error);
            toast.error('Error removing access. Please try again.');
        }
    };

    // Handle readability link click
    const handleReadabilityClick = async (file) => {
        setLoading(true);
        try {
            // Make API call to get full file details including page_details
            const response = await getFileDetails(file.id);
            const fullFileData = response.data;
            setSelectedFile(fullFileData);
            setShowModal(true);
        } catch (error) {
            console.error('Error fetching file details:', error);
            // You might want to show an error toast/alert here
            alert('Error loading file details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Close modal
    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedFile(null);
    };
    return (
        <>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <div className="container-fuild mb-5 px-3">
                <div className="row g-5 pt-3">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header pt-3 bg-white">

                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h4 className="text-secondary">Resources Directory</h4>
                                    </div>
                                    <div>
                                        <div className="form-check form-switch">
                                            <div className="form-check form-switch ps-0">
                                                <input className="form-check-input" type="checkbox" id="toggleHint" onChange={toggleHints} checked={showHints} />
                                                <label className="form-check-label" id="hintLabel" for="toggleHint" checked="">{showHints ? "Hide Hints" : "Show Hints"}</label>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                                {showHints && (
                                    <>
                                        <div className="card p-3 bg-light mb-2 mt-2">
                                            <div className="d-flex justify-content-between">
                                                <div className="w-75">
                                                    <p className="text-mute mb-0"><strong>Helpful Hint:</strong> AccessEVA works most
                                                        effectively and accurately when using scanned and created PDF and text files. For spreadsheets with “active”
                                                        formulas and for presentations similar to PowerPoint, AccessEVA still provides appropriate responses to queries but
                                                        may attempt to query and calculate answers which ability to accurately do so may be imperfect as a current
                                                        limitation of AI technology. Therefore, AccessEVA recommends that any spreadsheets and presentations be in PDF
                                                        format to ensure complete and accurate answers to queries.</p>
                                                </div>
                                                <div className="">
                                                    <button type="button" className="btn btn-outline-primary mb-2" onClick={handleUploadShow}>Add Files to Resources</button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card p-3 bg-light">
                                            <div className="d-flex justify-content-between">
                                                <div className="w-75">
                                                    <p className="text-mute mb-0"><strong>Helpful Hint:</strong> When creating a “New Project”,
                                                        please click select the existing vectored files from the below list before clicking the “Create New Project” button.  </p>
                                                </div>
                                                <div className="">
                                                    <button type="button" className="btn btn-outline-primary" style={{ paddingLeft: '1.4rem', paddingRight: '1.4rem' }} onClick={handleProjectShow}>Create New Project</button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-12 helpful-hint mt-2">
                                            <div className="card p-3 bg-light">
                                                <p className="text-mute mb-0"><strong>Helpful Hint:</strong> When selecting files to “Batch” Vector all of those files at once, select up to 5 files&nbsp;or&nbsp;a maximum of 10 MB for all selected files.</p>
                                            </div>
                                        </div>
                                        <div className="col-12 helpful-hint mt-2">
                                            <div className="card p-3 bg-light">
                                                <p className="text-mute mb-0"><strong>Helpful Hint:</strong> Embedded/active/live “links” (“Links”) in any selected files will prevent AccessEVA from vectoring the data of those selected files.&nbsp; For any “Project” or the “Resources”, please remove/deactivate all Links from any selected files&nbsp;before&nbsp;clicking “Vector”.&nbsp; One way to remove Links is to print the selected file and then scan those printed pages into a PDF. </p>
                                            </div>
                                        </div>

                                    </>
                                )}

                            </div>
                            <div className="card-header d-flex justify-content-between align-items-center">

                                <div className="d-flex align-items-center">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm  me-3"
                                        placeholder="File Name Search"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ width: '300px' }}
                                    />
                                    {isEnterpriseUser && (
                                        <Dropdown>
                                            <Dropdown.Toggle variant="outline-secondary" id="filter-dropdown" size="sm">
                                                {fileFilter === 'all' ? 'All Files' :
                                                    fileFilter === 'owned' ? 'My Files' : 'Shared With Me'}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item active={fileFilter === 'all'} onClick={() => setFileFilter('all')}>All Files</Dropdown.Item>
                                                <Dropdown.Item active={fileFilter === 'owned'} onClick={() => setFileFilter('owned')}>My Files</Dropdown.Item>
                                                <Dropdown.Item active={fileFilter === 'shared'} onClick={() => setFileFilter('shared')}>Shared With Me</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}
                                </div>
                                {!showHints &&
                                    <div>
                                        <button type="button" className="btn btn-outline-primary m-2" onClick={handleUploadShow}>Add Files to Resources</button>
                                        <button type="button" className="btn btn-outline-primary" onClick={handleProjectShow}>Create New Project</button>
                                    </div>
                                }
                            </div>

                            <div className="table-responsive position-relative">
                                <Table className="table table-borderless table-thead-bordered table-nowrap table-align-middle card-table table-striped mb-0">
                                    <thead className="thead-light border-bottom">
                                        <tr>
                                            <th className="ps-3">
                                                {/* <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={allSelected}
                                                    onChange={toggleSelectAll}
                                                /> */}
                                            </th>
                                            <th>Files Previously Vectored</th>
                                             {isEnterpriseUser && (
                                                <th>Readability</th>
                                            )}
                                            <th>Size/Page</th>
                                            <th>Vector Date</th>
                                            {/* <th>Last Used</th>
                                            <th>Uses</th> */}
                                            {isEnterpriseUser && (
                                                <th>Visibility</th>
                                            )}
                                            <th>Status</th>
                                            <th>Function</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentLists.map((file, index) => (
                                            <tr key={index}>
                                                <td className="ps-3"> <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={selectedFiles.includes(file.id)}
                                                    onChange={() => handleFileSelect(file.id)}
                                                /></td>
                                                <td>{file.name}</td>
                                                {isEnterpriseUser && (
                                                    <td>
                                                        {file.readability !== null && file.readability !== undefined ? (
                                                            Math.round(file.readability * 10) / 10 === 100 ? (
                                                                <span className="text-success">100%</span>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0 text-decoration-underline"
                                                                    onClick={() => handleReadabilityClick(file)}
                                                                >
                                                                    {formatReadability(file.readability)}
                                                                </button>
                                                            )
                                                        ) : (
                                                            <span className="text-muted">100%</span>
                                                        )}
                                                    </td>
                                                )}
                                                <td>{formatFileSize(file.size)}</td>
                                                <td>{new Date(file.uploadDate).toLocaleDateString()}</td>
                                                {/* <td>{file.lastUsed || 'N/A'}</td>
                                                <td>{file.uses || 0}</td> */}
                                                <td>{file.visibility}
                                                    {isEnterpriseUser && (
                                                        <span className="ms-2">
                                                            {(file.isShared &&
                                                                <Badge bg="info">Shared</Badge>
                                                            )}
                                                        </span>
                                                    )}
                                                </td>
                                                <td> {renderFileStatus(file)}</td>
                                                <td>

                                                    <div className="d-flex justify-content-start gap-2">
                                                        {file.visibility === "private" && !file.isShared && (
                                                            <>
                                                                <button type="button" className="btn btn-outline-warning btn-sm icon-btn" onClick={() => handleEditShow(file)} data-bs-toggle="tooltip" data-bs-placement="bottom" title="Edit">

                                                                    <i className="bi bi-pencil"></i>

                                                                </button>
                                                                <button type="button" className="btn btn-outline-primary btn-sm icon-btn" onClick={() => handleShareShow(file)} data-bs-toggle="tooltip" data-bs-placement="bottom" title="Share">
                                                                    <i className="bi bi-share"></i>
                                                                </button>
                                                                <button type="button" className="btn btn-outline-danger btn-sm icon-btn" onClick={() => handleDeleteShow(file)} data-bs-toggle="tooltip" data-bs-placement="bottom"
                                                                    title={isFileProcessing(file) ? "Cannot delete while processing" : "Delete"}
                                                                    disabled={isFileProcessing(file)}
                                                                >

                                                                    <i className="bi bi-trash"></i>

                                                                </button>
                                                            </>
                                                        )}
                                                        {file.isShared && (
                                                            <button type="button" className="btn btn-outline-warning btn-sm icon-btn" onClick={() => handleDetailsShow(file)} data-bs-toggle="tooltip" data-bs-placement="bottom" title="View Details">
                                                                <i className="bi bi-eye"></i>
                                                            </button>
                                                        )}
                                                    </div>

                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <div className="card-footer d-flex justify-content-between align-items-center bg-white">
                                    <div>
                                        <Pagination>
                                            {pageNumbers.map(number => (
                                                <Pagination.Item key={number} active={number === currentPage} onClick={() => paginate(number)}>
                                                    {number}
                                                </Pagination.Item>
                                            ))}
                                        </Pagination>
                                    </div>
                                    <div>

                                        <button type="button" className="btn btn-outline-primary m-2" onClick={handleUploadShow}>Add Files to Resources</button>
                                        <button type="button" className="btn btn-outline-primary" onClick={handleProjectShow}>Create New Project</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            {/* Upload Modal */}
            <Modal show={showUploadModal}
                onHide={handleUploadClose}
                backdrop="static" centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{isUploading ? "Vectoring..." : "Vector Files"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <UploadFilesTreading
                        onUploadComplete={handleUploadComplete}
                        onCancel={() => {
                            setIsUploading(false);
                            handleUploadClose();
                        }}
                        onUploadingStatusChange={handleUploadingStatus}
                        projectId='' />

                    {/* Display currently processing files in the modal */}
                    {processingFiles.length > 0 && (
                        <div className="mt-4">
                            <h5>Files Currently Processing: {processingFiles.length}</h5>

                            <div className="file-status-list mt-3">
                                {processingFiles.map(file => (
                                    <div key={file.id} className="file-status-item mb-2">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="file-name">{file.name}</span>
                                            <small>{file.status} {file.progress ? `(${file.progress}%)` : ''}</small>
                                        </div>

                                        <ProgressBar
                                            now={file.progress}
                                            variant={
                                                file.status === 'completed' ? 'success' :
                                                    file.status === 'error' ? 'danger' : 'info'
                                            }
                                            animated={file.status !== 'completed' && file.status !== 'error'}
                                            style={{ height: '0.5rem' }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={handleEditClose} centered>
                <Form onSubmit={handleEditSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Name and/or Visibility</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>File Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                defaultValue={currentFile?.name || ''}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Visibility</Form.Label>
                            <Form.Select
                                name="visibility"
                                defaultValue={currentFile?.visibility || 'private'}
                            >
                                <option value="private">Private</option>
                                <option value="global">Global</option>
                            </Form.Select>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        {/* <Button variant="secondary" onClick={handleEditClose}>
                            Close
                        </Button> */}
                        <Button variant="primary" type="submit">
                            Save Changes
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={handleDeleteClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete the file "{currentFile?.name}"?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleDeleteClose}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteFile}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Project Modal */}
            <Modal show={showProjectModal} onHide={handleProjectClose} centered>
                <Form onSubmit={handleProjectSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>New Project</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Project Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                required
                                defaultValue=''
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="description"
                                rows={3}
                                defaultValue=''
                            />
                        </Form.Group>
                        {/* <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                name="status"
                                defaultValue='Active'
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </Form.Select>
                        </Form.Group> */}
                        <Form.Group className="mb-3">
                            <Form.Label>Selected Files</Form.Label>
                            <ul>
                                {selectedFiles.map(fileId => {
                                    const file = filteredFiles.find(f => f.id === fileId);
                                    return <li key={fileId}>{file?.name}</li>;
                                })}
                            </ul>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        {/* <Button variant="secondary" onClick={handleProjectClose}>
                            Close
                        </Button> */}
                        <Button variant="primary" type="submit">
                            Save New Project
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* File Sharing Modal */}
            <Modal show={showShareModal} onHide={handleShareClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Share File</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <div className="mb-3">
                        <div>
                            <Form.Group className="mb-3">
                                <Form.Label>Select User</Form.Label>
                                <Form.Select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                >
                                    <option value="">Select a user</option>
                                    {allUsers.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.display_name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Button variant="primary" onClick={handleShareFile}>Share</Button>
                        </div>

                    </div>

                    {sharedUsers.length > 0 ? (
                        <div className="mt-4">
                            <h5 className="mt-4 mb-3">Shared With</h5>
                            <div className="list-group">
                                {sharedUsers.map(user => (
                                    <div key={user.id} className="list-group-item d-flex justify-content-between align-items-center">
                                        <div>
                                            <div>{user.name}</div>
                                            <small className="text-muted">
                                                {user.email}
                                            </small>
                                        </div>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleRemoveShare(user.user_id)}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p className="text-muted">This file isn't shared with anyone yet.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleShareClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal show={showDetailsModal} onHide={handleDetailsClose} centered >
                <Modal.Header closeButton>
                    <Modal.Title>File Details </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentFile && (
                        <div>
                            <div className="mb-4">
                                <div className="row">
                                    <div className="col-md-12">
                                        <p><strong>File Name:</strong> {currentFile.name}</p>
                                        <p><strong>Vector Date:</strong> {new Date(currentFile.uploaded_at).toLocaleDateString()}</p>
                                        <p><strong>Shared By:</strong> {currentFile.ownerName || "Project Owner"} ({currentFile.ownerEmail})</p>
                                        <p><strong>Shared On:</strong> {currentFile.isShared ? new Date(currentFile.shared_at).toLocaleDateString() : 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleDetailsClose}>
                        Close
                    </Button>

                </Modal.Footer>
            </Modal>


            <OCRDetailsModal
                show={showModal}
                onHide={handleCloseModal}
                fileData={selectedFile}
            />
        </>
    );
}

export default Resources;
